import React, { useEffect, useRef, useState } from 'react';
import { photos } from '../data';
import './Scene.css';

/*
  Fixed photo backdrop. Every section declares a photo with data-scene="<key>".
  The first section that hasn't fully scrolled above the middle of the viewport
  picks the photo; the layers crossfade and drift slightly with scroll.
*/

const KEYS = Object.keys(photos);

export default function Scene() {
  const root = useRef(null);
  const [active, setActive] = useState(KEYS[0]);

  useEffect(() => {
    const el = root.current;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let frame = 0;

    const update = () => {
      frame = 0;
      const mid = window.innerHeight * 0.5;
      const sections = document.querySelectorAll('[data-scene]');
      // The gap below a section belongs to the next one, so a new peak shows
      // up as soon as you scroll past the previous panel
      let next = null;
      for (const s of sections) {
        if (s.getBoundingClientRect().bottom >= mid) { next = s.dataset.scene; break; }
      }
      // Near the very bottom, the last section wins even if it is short
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4 && sections.length) {
        next = sections[sections.length - 1].dataset.scene;
      }
      if (next) setActive(next);

      const max = document.documentElement.scrollHeight - window.innerHeight;
      const p = still || max <= 0 ? 0 : Math.min(1, window.scrollY / max);
      el.style.setProperty('--p', p.toFixed(4));
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <>
      <div className="scene" ref={root} aria-hidden="true">
        {KEYS.map((key) => (
          <div
            key={key}
            className={`scene__photo${key === active ? ' is-active' : ''}`}
            style={{
              '--img': `url(${photos[key].src}.jpg)`,
              '--img-sm': `url(${photos[key].src}-sm.jpg)`,
              backgroundPosition: photos[key].focus,
            }}
          />
        ))}
      </div>
      <p className="scene__caption" aria-live="polite">
        <span className="scene__pin" aria-hidden="true" />
        {photos[active].place || (photos[active].mine ? 'My photo' : '')}
      </p>
    </>
  );
}
